// Best-effort telemetry queue. Records are batched into POST /api/kiosk/events
// (max 20 events and MAX_BATCH_BYTES serialized per request) after a short
// delay so a sale's burst of events goes out as one request. Plain fetch, no
// keepalive: the Android WebView never unloads the page, and keepalive on a
// preflighted cross-origin request is unreliable and capped at 64 KB. Every
// failure is swallowed: this must never block or break the purchase flow
// (record() included). A request that exceeds TELEMETRY_TIMEOUT_MS is
// aborted by ApiClient and counts as a network failure. Each failed batch is
// retried once, RETRY_DELAY_MS later (by its own timer, or by an explicit
// flush once the delay has passed), then dropped; a batch rejected with 413
// is split in halves and both are retried at the next flush, while a single
// event rejected with 413 is dropped. Requests never overlap.
import type {
  KioskEvent,
  KioskEventKind,
  KioskEventsRequest,
  KioskEventsResponse,
} from "@entradart/kiosk-shared/api";

export const MAX_STRING_CHARS = 4096;
// Below the route's 64 KB body limit, with headroom.
export const MAX_BATCH_BYTES = 56 * 1024;
const MAX_BATCH = 20;
// Delay before a batch that failed once is sent again.
export const RETRY_DELAY_MS = 5000;
// Serialized size of `{"events":[]}`.
const ENVELOPE_BYTES = 13;

type Poster = {
  postEvents(body: KioskEventsRequest): Promise<KioskEventsResponse>;
};

const encoder = new TextEncoder();

function byteLength(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length;
}

export type EventLogOptions = {
  flushDelayMs?: number;
  maxQueue?: number;
  now?: () => Date;
};

export function truncateStrings(
  value: unknown,
  max = MAX_STRING_CHARS
): unknown {
  if (typeof value === "string") {
    return value.length > max ? value.slice(0, max) : value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => truncateStrings(v, max));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = truncateStrings(v, max);
    }
    return out;
  }
  return value;
}

export class EventLog {
  private readonly api: Poster;
  private readonly flushDelayMs: number;
  private readonly maxQueue: number;
  private readonly now: () => Date;
  private queue: KioskEvent[] = [];
  // Batches that failed once; each gets exactly one more attempt, not
  // before dueAt (epoch ms).
  private retry: { batch: KioskEvent[]; dueAt: number }[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<void> | null = null;

  constructor(api: Poster, opts: EventLogOptions = {}) {
    this.api = api;
    this.flushDelayMs = opts.flushDelayMs ?? 500;
    this.maxQueue = opts.maxQueue ?? 100;
    this.now = opts.now ?? (() => new Date());
  }

  record(
    kind: KioskEventKind,
    data: Record<string, unknown>,
    orderId?: number
  ): void {
    try {
      const event: KioskEvent = {
        at: this.now().toISOString(),
        kind,
        data: truncateStrings(data) as Record<string, unknown>,
      };
      if (orderId !== undefined) {
        event.orderId = orderId;
      }
      this.queue.push(event);
      while (this.queue.length > this.maxQueue) {
        this.queue.shift();
      }
      if (this.timer === null) {
        this.timer = setTimeout(() => {
          this.timer = null;
          this.flush();
        }, this.flushDelayMs);
      }
    } catch {
      // Telemetry must never break the caller (e.g. circular data, bad clock).
    }
  }

  flush(): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.drain()
      .catch(() => {
        // Never reject: telemetry is best-effort.
      })
      .finally(() => {
        this.inFlight = null;
        this.scheduleRetry();
      });
    return this.inFlight;
  }

  private async drain(): Promise<void> {
    const now = Date.now();
    const due = this.retry.filter((r) => r.dueAt <= now);
    this.retry = this.retry.filter((r) => r.dueAt > now);
    for (const { batch } of due) {
      await this.send(batch, false);
    }
    while (this.queue.length > 0) {
      const batch = this.takeBatch();
      if (batch.length > 0) {
        await this.send(batch, true);
      }
    }
  }

  // Arms one timer for the earliest pending retry. Called after every flush,
  // so a retry that was not yet due (or arrived during a flush) is not left
  // waiting for the next record().
  private scheduleRetry(): void {
    try {
      if (this.retryTimer !== null || this.retry.length === 0) {
        return;
      }
      const earliest = Math.min(...this.retry.map((r) => r.dueAt));
      this.retryTimer = setTimeout(
        () => {
          this.retryTimer = null;
          this.flush();
        },
        Math.max(0, earliest - Date.now())
      );
    } catch {
      // Telemetry must never break the caller.
    }
  }

  // Keeps the events waiting for a retry within maxQueue, dropping the oldest
  // batches first, so a long outage cannot grow memory without bound.
  private pushRetry(batch: KioskEvent[], dueAt: number): void {
    this.retry.push({ batch, dueAt });
    let total = this.retry.reduce((n, r) => n + r.batch.length, 0);
    while (total > this.maxQueue && this.retry.length > 1) {
      total -= this.retry[0].batch.length;
      this.retry.shift();
    }
  }

  // Takes events from the head of the queue while the serialized batch stays
  // within MAX_BATCH_BYTES and MAX_BATCH. An event larger than the budget on
  // its own is sent alone (it cannot be split). An event that cannot be
  // serialized (e.g. a BigInt in data) is dropped here, since it would fail
  // every request it is part of.
  private takeBatch(): KioskEvent[] {
    let size = ENVELOPE_BYTES;
    let count = 0;
    while (count < this.queue.length && count < MAX_BATCH) {
      let next: number;
      try {
        next = byteLength(this.queue[count]) + (count > 0 ? 1 : 0);
      } catch {
        this.queue.splice(count, 1);
        continue;
      }
      if (count > 0 && size + next > MAX_BATCH_BYTES) {
        break;
      }
      size += next;
      count++;
    }
    return this.queue.splice(0, count);
  }

  private async send(batch: KioskEvent[], allowRetry: boolean): Promise<void> {
    try {
      await this.api.postEvents({ events: batch });
    } catch (e) {
      if ((e as { status?: unknown } | null)?.status === 413) {
        // Too large for the route: retrying unchanged would fail again. Split
        // it (halves always shrink, so this terminates); a lone event is
        // dropped. Not a transient failure, so the halves need no backoff.
        if (batch.length > 1) {
          const half = Math.ceil(batch.length / 2);
          const now = Date.now();
          this.pushRetry(batch.slice(0, half), now);
          this.pushRetry(batch.slice(half), now);
        }
        return;
      }
      if (allowRetry) {
        this.pushRetry(batch, Date.now() + RETRY_DELAY_MS);
      }
    }
  }
}
